import filters, {
  createFilter,
  getSubOption,
  mapSubcategoryToCategory,
} from 'data/filters';
import { Filter } from 'model/Filters';
import deepClone from 'utils/deepClone';
import slugify from 'utils/slugify';
import uniqueBy from 'utils/uniqueBy';

type PathAndQuery = { pathParts: string[]; queryParts: URLSearchParams };

const MAIN_FILTERS = [
  'activityType.activity',
  'subcategory.items[]',
  'tags.items[]',
];

const OTHER_FILTERS = [
  'duration.items[]',
  'price.items[]',
  'family-friendly',
  'languages.items[]',
  'other.transport_mode.items[]',
  'other.availability.items[]',
];

const PATH_FILTERS = [...MAIN_FILTERS, ...OTHER_FILTERS];

function filtersSorter(a: Filter, b: Filter): -1 | 0 | 1 {
  const indexA = filters.findIndex((f) => f.uid === a.uid);
  if (indexA < 0) {
    console.error('unable to find filter ', JSON.stringify(a));
  }
  const indexB = filters.findIndex((f) => f.uid === b.uid);
  if (indexB < 0) {
    console.error('unable to find filter ', JSON.stringify(b));
  }

  if (indexA < indexB) {
    return -1;
  }
  if (indexA > indexB) {
    return 1;
  }
  return 0;
}

function getValues(filter: Filter): string[] {
  if (filter?.subOptions?.length) {
    return filter.subOptions.map(({ value }) => value);
  }
  if (filter.queryName === undefined) {
    return [filter.uid];
  }
  if (!!filter.value) {
    return [encodeURIComponent(filter.value)];
  }
  if (filter.value !== 0) {
    console.warn(
      'Invalid filter found. Both value and subOptions not specified',
      filter,
    );
  }
  return [];
}

function addAllToParams(
  params: URLSearchParams,
  key: string,
  values: string[] = [],
) {
  values.forEach((value) => params.append(key, value));
}

function shouldGoToPath(uid, isCategorySet) {
  return (
    PATH_FILTERS.includes(uid) && !(uid === 'tags.items[]' && isCategorySet)
  );
}

function addCategoryIfNeeded(filters) {
  const subcategories = filters.find(
    ({ uid }) => uid === 'subcategory.items[]',
  ) || { subOptions: [] };
  const categories = subcategories.subOptions.reduce((result, { value }) => {
    const matchingCategory = mapSubcategoryToCategory[value];
    if (matchingCategory && !result.includes(matchingCategory)) {
      return [...result, matchingCategory];
    }
    return result;
  }, []);
  const filtersToAdd: Filter[] = [];
  if (categories.length) {
    let categoryFilter = filters.find(
      ({ uid }) => uid === 'activityType.activity',
    );
    if (!categoryFilter) {
      categoryFilter = createFilter('activityType.activity');
      filtersToAdd.push(categoryFilter);
    }
    const newSubOptions = categories.map((category) =>
      getSubOption('activityType.activity', category),
    );
    categoryFilter.subOptions = uniqueBy(
      [...newSubOptions, ...categoryFilter.subOptions],
      (option) => option.value,
    );
  }

  return [...filtersToAdd, ...filters];
}

function compileFilters(filters: Filter[]): PathAndQuery {
  const sortedFilters = addCategoryIfNeeded(filters.sort(filtersSorter));
  const isCategorySet = sortedFilters?.[0]?.uid === 'activityType.activity';
  let hasOneOtherFilter = false;
  const { pathParts, queryParts } = sortedFilters.reduce(
    ({ pathParts, queryParts }, filter) => {
      const values = getValues(filter);
      const defaultValue =
        filter.defaultValue === undefined ? [] : [String(filter.defaultValue)];
      const valuesAreDefault =
        values.length === defaultValue.length &&
        values.every((value, index) => value === defaultValue[index]);
      if (!valuesAreDefault) {
        if (
          shouldGoToPath(filter.uid, isCategorySet) &&
          !(hasOneOtherFilter && OTHER_FILTERS.includes(filter.uid))
        ) {
          hasOneOtherFilter = OTHER_FILTERS.includes(filter.uid);
          const [first, ...rest] = getValues(filter);
          pathParts.push(
            filter.queryName === 'language[]'
              ? decodeURIComponent(first)
              : first,
          );
          if (rest.length) {
            addAllToParams(queryParts, filter.queryName, rest);
          }
        } else {
          addAllToParams(queryParts, filter.queryName, values);
        }
      }
      return { pathParts, queryParts };
    },
    { pathParts: [], queryParts: new URLSearchParams() },
  );

  return { pathParts, queryParts };
}

function compile(
  country: string | null,
  city: string | null,
  page: number,
  filters: Filter[],
  extraQueryParams?: URLSearchParams | null,
): string {
  const { pathParts, queryParts } = compileFilters(deepClone(filters));

  if (extraQueryParams) {
    extraQueryParams.forEach((value, key) => queryParts.append(key, value));
  }
  if (page > 1) {
    pathParts.push(page.toString());
  }

  const path = [
    slugify(country || '', false),
    slugify(city || '', false),
    ...pathParts,
  ]
    .filter((val) => !!val)
    .join('/');
  const query =
    queryParts.toString().length > 0
      ? `?${decodeURIComponent(queryParts.toString())}`
      : '';

  return '/' + (path ? path + '/' : '') + query;
}

export default { compile };
